const ALL_ROLES = ['hierarchy', 'sequence', 'dependency', 'success', 'failure'];

export function runbookEdgeRole(edge) {
    const kind = edge?.data?.kind ?? edge?.kind ?? 'relates';
    const id = String(edge?.id ?? '');

    if (kind === 'contains') return 'hierarchy';
    if (kind === 'on_failure') return 'failure';
    if (kind === 'on_success') return 'success';
    if (kind === 'depends_on' || id.startsWith('rbflow-req-')) return 'dependency';
    if (kind === 'flows_to') return 'sequence';

    return null;
}

export function runbookHierarchy(edges) {
    const children = new Map();
    const parent = new Map();

    for (const edge of edges ?? []) {
        if (edge?.data?.projection || runbookEdgeRole(edge) !== 'hierarchy') continue;
        if (!children.has(edge.source)) children.set(edge.source, []);
        children.get(edge.source).push(edge.target);
        parent.set(edge.target, edge.source);
    }

    return { children, parent };
}

export function runbookDescendants(nodeId, children) {
    const found = [];
    const pending = [...(children.get(nodeId) ?? [])];
    const seen = new Set();

    while (pending.length) {
        const id = pending.shift();
        if (!id || seen.has(id)) continue;
        seen.add(id);
        found.push(id);
        pending.push(...(children.get(id) ?? []));
    }

    return found;
}

/**
 * How a freshly generated flow opens: the top view, and only the top view.
 *
 * Everything with children starts collapsed, whatever the size of the runbook. This is the
 * canvas's admission control — a flow is generated once and opened many times, and mounting
 * every step and sub-step of a big one on first paint is hundreds of cards of DOM before the
 * person has asked to see any of them. Opening is the batched expansion, which reveals in
 * task order with live progress; collapsed-by-default is what makes that the ONLY path in.
 *
 * There used to be a size threshold (small flows opened with phases expanded). It went: the
 * first paint of a flow should be the same shape at 12 steps as at 800, and the overview IS
 * the top view. Expanding is one click and arrives tidied.
 */
export function defaultRunbookCollapse(nodes, edges) {
    const { children } = runbookHierarchy(edges);

    return new Set([...children.keys()]);
}

export function restoreRunbookCollapse(serialized, fingerprint, fallback) {
    try {
        const saved = JSON.parse(serialized ?? 'null');
        if (saved?.fingerprint !== fingerprint || !Array.isArray(saved?.ids)) {
            return new Set(fallback);
        }

        return new Set(saved.ids.map(String));
    } catch {
        return new Set(fallback);
    }
}

export function runbookExecutionOrder(nodes) {
    return [...(nodes ?? [])].sort((left, right) => {
        const leftMarker = left?.data?.marker;
        const rightMarker = right?.data?.marker;
        if (leftMarker === 'start' || rightMarker === 'end') return -1;
        if (leftMarker === 'end' || rightMarker === 'start') return 1;

        const leftNumber = String(left?.data?.stepNumber ?? '');
        const rightNumber = String(right?.data?.stepNumber ?? '');
        if (leftNumber !== '' && rightNumber !== '') {
            return leftNumber.localeCompare(rightNumber, undefined, { numeric: true });
        }
        if (leftNumber !== '') return -1;
        if (rightNumber !== '') return 1;

        return 0;
    });
}

export function runbookStartNodeId(nodes) {
    const visible = (nodes ?? []).filter((node) => !node?.hidden);
    const marker = visible.find((node) => node?.data?.marker === 'start');

    return marker?.id ?? runbookExecutionOrder(visible)[0]?.id ?? null;
}

export function resolveRunbookConnection(edges, source, target, requestedKind = 'relates') {
    const kind = requestedKind === 'relates' ? 'flows_to' : requestedKind;
    const canonical = (edges ?? []).filter((edge) => !edge?.data?.projection
        && edge.source === source && edge.target === target);
    const existing = canonical.find((edge) => (edge.data?.kind ?? 'relates') === kind) ?? null;
    const replace = existing === null && kind === 'flows_to'
        ? canonical.find((edge) => (edge.data?.kind ?? 'relates') === 'relates') ?? null
        : null;

    return { kind, existing, replace };
}

export function nextRunbookExpansion(nodes, visibleIds, batchSize = 24) {
    const visible = new Set(visibleIds ?? []);
    const pending = runbookExecutionOrder(nodes).filter((node) => !visible.has(node.id));

    for (const node of pending.slice(0, Math.max(1, batchSize))) {
        visible.add(node.id);
    }

    return { visible, done: pending.length <= Math.max(1, batchSize) };
}

export function nextRunbookBranchExpansion(
    nodes,
    edges,
    nodeId,
    visibleIds,
    batchSize = 24,
    collapsed = new Set(),
) {
    const { children } = runbookHierarchy(edges);
    const closed = collapsed instanceof Set ? collapsed : new Set(collapsed ?? []);
    const branchIds = new Set();
    const pending = [...(children.get(nodeId) ?? [])];

    while (pending.length) {
        const id = pending.shift();
        if (!id || branchIds.has(id)) continue;
        branchIds.add(id);
        if (!closed.has(id)) pending.push(...(children.get(id) ?? []));
    }

    const branchNodes = (nodes ?? []).filter((node) => branchIds.has(node.id));

    return nextRunbookExpansion(branchNodes, visibleIds, batchSize);
}

export function runbookDependencyNeighborhood(edges, nodeId) {
    const ids = new Set(nodeId ? [nodeId] : []);

    for (const edge of edges ?? []) {
        if (runbookEdgeRole(edge) !== 'dependency'
            || edge.source !== nodeId && edge.target !== nodeId) continue;
        ids.add(edge.source);
        ids.add(edge.target);
    }

    return ids;
}

function relationshipItem(node, relation, kind) {
    return {
        nodeId: node.id,
        entryId: node.data?.entryId ?? null,
        title: node.data?.title ?? 'Step',
        stepNumber: node.data?.stepNumber ?? '',
        relation,
        kind,
    };
}

export function runbookRelationshipSummaries(nodes, edges, limit = 3) {
    const byId = new Map((nodes ?? []).map((node) => [node.id, node]));
    const items = new Map((nodes ?? []).map((node) => [node.id, new Map()]));
    const add = (ownerId, neighborId, relation, kind) => {
        const owner = byId.get(ownerId);
        const neighbor = byId.get(neighborId);
        if (!owner || owner.data?.marker || !neighbor || neighbor.data?.marker || !items.has(ownerId)) return;
        items.get(ownerId).set(
            `${relation}:${neighborId}`,
            relationshipItem(neighbor, relation, kind),
        );
    };

    for (const edge of (edges ?? []).filter((item) => !item?.data?.projection)) {
        let role = runbookEdgeRole(edge);
        const sourceEntryId = Number(byId.get(edge.source)?.data?.entryId ?? 0);
        const targetRequirements = (byId.get(edge.target)?.data?.requiresEntryIds ?? [])
            .map(Number);
        if (role === 'sequence' && targetRequirements.includes(sourceEntryId)) {
            role = 'dependency';
        }
        if (!role || role === 'hierarchy') continue;
        if (role === 'dependency') {
            add(edge.target, edge.source, 'prerequisite', 'dependency');
            add(edge.source, edge.target, 'successor', 'dependency');
            continue;
        }
        const kind = role === 'success' || role === 'failure' ? role : 'sequence';
        add(edge.target, edge.source, 'predecessor', kind);
        add(edge.source, edge.target, 'successor', kind);
    }

    return new Map((nodes ?? []).map((node) => {
        const all = [...(items.get(node.id)?.values() ?? [])];

        return [node.id, {
            items: all.slice(0, Math.max(0, limit)),
            total: all.length,
            requiresApproval: node.data?.requiresApproval === true,
        }];
    }));
}

function applyManualVisibility(hidden, manuallyHiddenIds, descendants) {
    for (const id of manuallyHiddenIds ?? []) {
        hidden.add(id);
        for (const descendant of descendants.get(id) ?? []) hidden.add(descendant);
    }
}

export function projectRunbookGraph(
    nodes,
    edges,
    collapsed = new Set(),
    enabledRoles = ALL_ROLES,
    visibleIds = null,
    manuallyHiddenIds = new Set(),
    focusIds = null,
) {
    const canonicalEdges = (edges ?? []).filter((edge) => !edge?.data?.projection);
    const relationships = runbookRelationshipSummaries(nodes, canonicalEdges);
    const { children, parent } = runbookHierarchy(canonicalEdges);
    const visible = visibleIds instanceof Set ? visibleIds : null;
    const focused = focusIds instanceof Set ? focusIds : null;
    const hidden = new Set((nodes ?? [])
        .filter((node) => focused ? !focused.has(node.id) : visible && !visible.has(node.id))
        .map((node) => node.id));
    const descendants = new Map();

    for (const id of children.keys()) {
        descendants.set(id, runbookDescendants(id, children));
    }
    if (!focused) {
        for (const id of collapsed) {
            for (const descendant of descendants.get(id) ?? []) hidden.add(descendant);
        }
    }
    applyManualVisibility(hidden, manuallyHiddenIds, descendants);

    const representative = (id) => {
        let current = id;
        let guard = 0;
        while (hidden.has(current) && parent.has(current) && guard++ < 100) {
            current = parent.get(current);
        }

        return current;
    };

    const blockers = new Map();
    for (const edge of canonicalEdges) {
        if (runbookEdgeRole(edge) !== 'dependency') continue;
        const source = representative(edge.source);
        const target = representative(edge.target);
        if (source === target) continue;
        blockers.set(target, (blockers.get(target) ?? 0) + 1);
    }

    const projectedNodes = (nodes ?? []).map((node) => {
        const childCount = children.get(node.id)?.length ?? 0;
        const descendantCount = descendants.get(node.id)?.length ?? 0;
        const data = {
            ...node.data,
            runbookFlow: true,
            runbookGroup: descendantCount > 0,
            runbookChildCount: childCount,
            runbookDescendantCount: descendantCount,
            runbookCollapsed: collapsed.has(node.id),
            runbookBlockerCount: blockers.get(node.id) ?? 0,
            runbookRelationships: relationships.get(node.id),
        };

        return { ...node, hidden: hidden.has(node.id), data };
    });

    const enabled = new Set(enabledRoles ?? ALL_ROLES);
    const aggregates = new Map();
    const projectedEdges = canonicalEdges.map((edge) => {
        const role = runbookEdgeRole(edge);
        const edgeData = { ...edge.data, runbookRole: role };
        const source = focused ? edge.source : representative(edge.source);
        const target = focused ? edge.target : representative(edge.target);
        const visible = role !== null && enabled.has(role) && !hidden.has(source) && !hidden.has(target)
            && source === edge.source && target === edge.target;

        if (!focused && role !== 'hierarchy' && role !== null && enabled.has(role) && source !== target
            && (source !== edge.source || target !== edge.target)) {
            const key = `${role}:${source}:${target}`;
            const current = aggregates.get(key);
            aggregates.set(key, current
                ? { ...current, data: { ...current.data, count: current.data.count + 1 } }
                : {
                    ...edge,
                    id: `runbook-projection-${key}`,
                    source,
                    target,
                    hidden: false,
                    data: { ...edgeData, projection: true, count: 1 },
                });
        }

        return { ...edge, hidden: !visible, data: edgeData };
    });

    return {
        nodes: projectedNodes,
        edges: [...projectedEdges, ...aggregates.values()],
        hidden,
        visibleNodeCount: projectedNodes.length - hidden.size,
        totalNodeCount: projectedNodes.length,
    };
}

export const RUNBOOK_EDGE_ROLES = ALL_ROLES;
